/**
 * サイトダッシュボードページ (Figma #27 準拠版).
 *
 * admin-api の GET /sites/:siteId を叩いてサイト情報を表示。
 * レイアウト：ステータスバナー → 4つのstat card → 最近の更新テーブル
 */

import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import {
  ApiError,
  authMe,
  fetchSite,
  fetchSiteMedia,
  fetchSitePosts,
  fetchSiteSnippets,
} from '../lib/api-client.js';

export function DashboardPage() {
  const params = useParams({ strict: false }) as { siteId?: string };
  const siteId = params.siteId ?? '';

  const meQuery = useQuery({ queryKey: ['me'], queryFn: authMe });

  const siteQuery = useQuery({
    queryKey: ['sites', siteId],
    queryFn: () => fetchSite(siteId),
    enabled: !!siteId,
  });

  const postsQuery = useQuery({
    queryKey: ['sites', siteId, 'posts', 'dashboard'],
    queryFn: () => fetchSitePosts(siteId, { limit: 10 }),
    enabled: !!siteId,
  });

  const snippetsQuery = useQuery({
    queryKey: ['sites', siteId, 'snippets'],
    queryFn: () => fetchSiteSnippets(siteId),
    enabled: !!siteId,
  });

  const mediaQuery = useQuery({
    queryKey: ['sites', siteId, 'media', 'dashboard'],
    queryFn: () => fetchSiteMedia(siteId, { limit: 1 }),
    enabled: !!siteId,
  });

  if (siteQuery.isLoading) {
    return <div className="text-sm text-[#706d65]">読み込み中...</div>;
  }

  if (siteQuery.error) {
    const is404 = siteQuery.error instanceof ApiError && siteQuery.error.status === 404;
    return (
      <div className="rounded-[6px] border-l-4 border-[#ef4444] bg-[#fef2f2] p-4 text-sm text-[#991b1b]">
        {is404 ? (
          <>
            サイトが見つかりませんでした。
            <Link to="/sites" className="ml-2 text-[#3b82f6] hover:underline">
              サイト選択に戻る →
            </Link>
          </>
        ) : (
          `エラー: ${String(siteQuery.error)}`
        )}
      </div>
    );
  }

  const site = siteQuery.data;
  if (!site) return null;

  const posts = postsQuery.data?.posts ?? [];
  const postsByStatus = {
    draft: posts.filter((p) => p.status === 'draft').length,
    published: posts.filter((p) => p.status === 'published').length,
  };

  const snippetsCount = snippetsQuery.data?.length ?? 0;
  const mediaCount = mediaQuery.data?.total ?? 0;

  // 最終更新日時と更新ユーザーを取得
  const latestPost = posts[0];
  const lastUpdatedDate = latestPost
    ? new Date(latestPost.updatedAt).toLocaleDateString('ja-JP')
    : '更新なし';
  const myUserId = meQuery.data?.id;
  const formatUser = (uid: string) => (uid === myUserId ? 'あなた' : uid.slice(0, 12) + '…');
  const lastUpdatedBy = latestPost ? formatUser(latestPost.authorUserId) : '';

  const isEmpty =
    posts.length === 0 && snippetsCount === 0 && mediaCount === 0 && !postsQuery.isLoading;

  return (
    <div className="space-y-6">
      {/* オンボーディング（全て空の場合のみ） */}
      {isEmpty && (
        <div className="rounded-[8px] border border-[#3b82f6] bg-[#eff6ff] px-6 py-5">
          <h2 className="text-base font-semibold text-[#1e40af]">
            🚀 セットアップを完了しましょう
          </h2>
          <p className="mt-1 text-sm text-[#1e40af]">
            以下の 3 ステップでサイトに PetaCMS を組み込めます。
          </p>
          <ol className="mt-4 space-y-3 text-sm">
            <li className="flex gap-3">
              <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-[#3b82f6] text-xs font-bold text-white">
                1
              </span>
              <div>
                <Link
                  to="/sites/$siteId/posts/new"
                  params={{ siteId }}
                  className="font-medium text-[#1e40af] hover:underline"
                >
                  最初の記事を作成する →
                </Link>
                <p className="mt-0.5 text-xs text-[#1e40af]">
                  タイトルと本文を入力するだけで投稿できます
                </p>
              </div>
            </li>
            <li className="flex gap-3">
              <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-[#3b82f6] text-xs font-bold text-white">
                2
              </span>
              <div>
                <Link
                  to="/sites/$siteId/snippets"
                  params={{ siteId }}
                  className="font-medium text-[#1e40af] hover:underline"
                >
                  スニペットを登録する →
                </Link>
                <p className="mt-0.5 text-xs text-[#1e40af]">
                  既存サイトのテキストをリモート更新可能にします
                </p>
              </div>
            </li>
            <li className="flex gap-3">
              <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-[#3b82f6] text-xs font-bold text-white">
                3
              </span>
              <div>
                <a
                  href="https://peta-cms.com/docs"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium text-[#1e40af] hover:underline"
                >
                  widget.js をサイトに貼る ↗
                </a>
                <p className="mt-0.5 text-xs text-[#1e40af]">
                  導入ガイドで具体的な手順を確認できます
                </p>
              </div>
            </li>
          </ol>
        </div>
      )}

      {/* ステータスバナー */}
      <div className="rounded-[8px] bg-[rgba(16,185,129,0.12)] px-5 py-4 text-sm text-[#065f46]">
        ✓ スクリプト設置済み — {site.originHost} との接続は正常です
      </div>

      {/* 4つのstat card */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {[
          {
            label: '記事数',
            value: posts.length,
            sub: `公開中: ${postsByStatus.published} / 下書き: ${postsByStatus.draft}`,
            loading: postsQuery.isLoading,
          },
          {
            label: 'スニペット数',
            value: snippetsCount,
            sub: '全て有効',
            loading: snippetsQuery.isLoading,
          },
          {
            label: '最終更新',
            value: lastUpdatedDate,
            sub: lastUpdatedBy ? `${lastUpdatedBy} が更新` : '',
            loading: postsQuery.isLoading,
          },
          {
            label: 'メディア',
            value: mediaCount,
            sub: 'ファイル登録済',
            loading: mediaQuery.isLoading,
          },
        ].map((card) => (
          <div
            key={card.label}
            className="min-h-[120px] rounded-[8px] border border-[#d6d3d0] bg-[#ffffff] px-5 py-6"
          >
            <div className="text-[13px] font-medium text-[#706d65]">{card.label}</div>
            {card.loading ? (
              <div className="mt-2 h-8 w-16 animate-pulse rounded bg-[#edebe8]" />
            ) : (
              <div className="mt-2 text-[28px] font-bold text-[#23221e]">{card.value}</div>
            )}
            <div className="mt-1 text-[12px] text-[#c1bdb7]">{card.loading ? '' : card.sub}</div>
          </div>
        ))}
      </div>

      {/* 最近の更新セクションタイトル */}
      <h2 className="text-[16px] font-semibold text-[#23221e]">最近の更新</h2>

      {/* Activity log テーブル */}
      <div className="rounded-[8px] border border-[#d6d3d0] bg-[#ffffff] overflow-hidden">
        {posts.length === 0 ? (
          <div className="px-5 py-8 text-center text-sm text-[#706d65]">
            まだ投稿がありません。
            <Link
              to="/sites/$siteId/posts/new"
              params={{ siteId }}
              className="ml-1 text-[#3b82f6] hover:underline"
            >
              最初の記事を作成しましょう！
            </Link>
          </div>
        ) : (
          <ul className="divide-y divide-[#edebe8]">
            {posts.slice(0, 10).map((post) => (
              <li
                key={post.id}
                className="flex items-center gap-4 px-5 py-4 hover:bg-[#f8f7f6] transition-colors"
              >
                {/* timestamp */}
                <div className="text-[13px] text-[#c1bdb7] whitespace-nowrap">
                  {new Date(post.updatedAt).toLocaleString('ja-JP')}
                </div>

                {/* action */}
                <div className="text-[14px] font-medium text-[#23221e] whitespace-nowrap">
                  {post.status === 'published'
                    ? '記事を公開'
                    : post.status === 'draft'
                      ? '下書き保存'
                      : post.status === 'trash'
                        ? '記事を削除'
                        : '記事を更新'}
                </div>

                {/* title as link */}
                <div className="min-w-0 flex-1">
                  <Link
                    to="/sites/$siteId/posts/$postId/edit"
                    params={{ siteId, postId: post.id }}
                    className="text-[14px] text-[#3b82f6] hover:underline truncate"
                  >
                    {post.title}
                  </Link>
                </div>

                {/* user */}
                <div className="text-[13px] text-[#706d65] whitespace-nowrap">
                  {formatUser(post.authorUserId)}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
